/**
 * `date` — `DateValue` (`specs/M2-tier1-column-types.md` §5.6, `[09§A.7 date]`).
 *
 * Verbatim from the notes: *"The `time` field and the time in `text` are automatically converted
 * to the time zone of the user making the API call. The `value` field always stores times in
 * UTC."* The recorded corpus proves it: `{"date":"2026-06-01","time":"09:30:00"}` came back as
 * `text: "2026-06-01 02:30"` and `{"time":"14:00:00"}` as `07:00` — the recording session
 * resolved at UTC-07:00 (`0077-…`, `0089-…`). So the canonical value is **UTC**, `toText` and
 * `toApiFragment` render in `ctx.timezone` (UTC when omitted), and the sort shadows stay UTC.
 *
 * `text` is `"YYYY-MM-DD"` for a date-only cell and `"YYYY-MM-DD HH:MM"` — **minutes, no
 * seconds** — when a time is set, while `value.time` keeps `HH:MM:SS` `[cvs§2 date]`.
 * In the typed fragment `time` is `""` for a date-only cell while `value.time` is `null`
 * (recorded in `0258-tier-b-fragments-date-like.json`); both are reproduced.
 */
import { z } from 'zod';
import { defaultFilterSql, defaultSummarySql } from '../sql.js';
import { formatDatePart, formatTimeHm, textShadow, wallClockIn, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;
function isRealDate(value) {
    if (!DATE_RE.test(value))
        return false;
    const [y, m, d] = value.split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}
import { dateFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const dateValueSchema = z.looseObject({
    date: z.string().refine(isRealDate, { message: 'expected YYYY-MM-DD' }),
    time: z.string().regex(TIME_RE, 'expected HH:mm or HH:mm:ss').nullable().optional(),
    icon: z.string().nullable().optional(),
    changed_at: z.string().optional(),
});
/** Settings keys documented for the date column `[09§A.7 date]`. */
export const dateSettingsSchema = z.looseObject({
    show_current_year: z.boolean().optional(),
    show_week_day: z.boolean().optional(),
    show_week_number: z.boolean().optional(),
    show_time_by_default: z.boolean().optional(),
    hide_footer: z.boolean().optional(),
    use_numeric_only_format: z.boolean().optional(),
    show_add_icon: z.boolean().optional(),
    show_weekends: z.boolean().optional(),
    calcType: z.enum(['earliestToLatest', 'earliest', 'latest']).optional(),
    date_format: z.string().optional(),
    time_format: z.string().optional(),
});
function normalizeTime(time) {
    const match = TIME_RE.exec(time);
    if (match === null)
        throw new ColumnValueError('date', `"${time}" is not a valid time`);
    const [, hh, mm, ss] = match;
    if (Number(hh) > 23 || Number(mm) > 59 || Number(ss ?? '0') > 59) {
        throw new ColumnValueError('date', `"${time}" is out of range`);
    }
    return `${hh}:${mm}:${(ss ?? '00').padStart(2, '0')}`;
}
function fromString(raw) {
    const trimmed = raw.trim();
    if (trimmed === '')
        return null;
    const [datePart, timePart] = trimmed.replace('T', ' ').replace(/Z$/, '').split(' ');
    if (datePart === undefined || !isRealDate(datePart)) {
        throw new ColumnValueError('date', `"${raw}" is not a valid date`);
    }
    return timePart === undefined || timePart === ''
        ? { date: datePart }
        : { date: datePart, time: normalizeTime(timePart) };
}
function normalize(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw === 'string')
        return fromString(raw);
    if (raw instanceof Date) {
        if (Number.isNaN(raw.getTime()))
            throw new ColumnValueError('date', 'invalid Date');
        const iso = raw.toISOString();
        return { date: iso.slice(0, 10), time: iso.slice(11, 19) };
    }
    if (typeof raw === 'object') {
        const record = raw;
        if (record.date === null || record.date === undefined || record.date === '')
            return null;
        if (typeof record.date !== 'string')
            throw new ColumnValueError('date', '"date" must be a string');
        if (!isRealDate(record.date)) {
            throw new ColumnValueError('date', `"${record.date}" is not a valid date`);
        }
        const value = { date: record.date };
        if (typeof record.time === 'string' && record.time !== '')
            value.time = normalizeTime(record.time);
        if (keepVolatile) {
            // monday ships `icon: null` and `changed_at` on almost every populated cell `[cvs§2 date]`.
            if ('icon' in record)
                value.icon = (record.icon ?? null);
            if (typeof record.changed_at === 'string')
                value.changed_at = record.changed_at;
        }
        return value;
    }
    throw new ColumnValueError('date', `expected an object or string, received ${typeof raw}`);
}
/** UTC instant for the `date_value` shadow column. Date-only cells sort at 00:00:00Z. */
export function toUtcDate(value) {
    return new Date(`${value.date}T${value.time ?? '00:00:00'}Z`);
}
/**
 * The date/time a viewer in `timezone` sees. A date-only cell has no instant, so it is never
 * shifted — monday's own `text` for a date-only cell is the stored day (`"2026-07-02"`).
 */
export function localParts(value, timezone) {
    if (value.time === undefined || value.time === null)
        return { date: value.date, time: null };
    const clock = wallClockIn(toUtcDate(value), timezone);
    return { date: formatDatePart(clock), time: formatTimeHm(clock) };
}
function renderText(value, ctx) {
    if (value === null)
        return '';
    const parts = localParts(value, ctx?.timezone);
    return parts.time === null ? parts.date : `${parts.date} ${parts.time}`;
}
export const dateColumn = defineColumnType({
    type: 'date',
    title: 'Date',
    description: 'A date with an optional time, stored in UTC.',
    apiFragmentName: 'DateValue',
    legacyTypeName: 'date',
    idPrefix: 'date',
    valueSchema: dateValueSchema,
    settingsSchema: dateSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: dateSettingsSchema,
    writable: true,
    supportsSimpleValue: true,
    clearForms: ['null', 'empty-string'],
    filterConditions: dateFilterConditions,
    filterOperators: operatorsOf(dateFilterConditions),
    aggregations: ['earliest', 'latest', 'range', 'count', 'count_empty', 'none'],
    emptyText: '',
    parseValue(raw) {
        return normalize(raw, false);
    },
    parseSimpleValue(raw) {
        return normalize(raw, false);
    },
    toText(value, _settings, ctx) {
        return renderText(value, ctx);
    },
    toSortKeys(value) {
        if (value === null)
            return { text: null, number: null, date: null };
        const utc = toUtcDate(value);
        // The shadows are timezone-independent: they are what SQL sorts and filters on.
        return { text: textShadow(renderText(value)), number: utc.getTime(), date: utc };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        return a.date === b.date && (a.time ?? null) === (b.time ?? null);
    },
    toActivityValue(value) {
        return value === null ? null : { date: value.date, time: value.time ?? null };
    },
    fromMondayValue(raw) {
        return normalize(raw, true);
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        // A date with no time and a date at midnight are different values — never normalise one
        // into the other `[M2 §10 case 9]`. monday expresses "no time" by **omitting the key**:
        // 42 of the 1,392 populated `date` cells in the 2026-09-10 snapshot ship
        // `{"date":"2023-02-28"}` with no `time` at all, and none ships `"time":null`. Emitting
        // the key was a `/v2` conformance difference (T-X2 defect 2).
        const { time, ...rest } = value;
        return time === null || time === undefined ? { ...rest } : { ...rest, time };
    },
    toSimpleValue(value) {
        if (value === null)
            return '';
        return value.time === undefined || value.time === null ? value.date : `${value.date} ${value.time}`;
    },
    fromMondaySettings(raw) {
        return dateSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value, _settings, ctx) {
        if (value === null)
            return {};
        const parts = localParts(value, ctx?.timezone);
        return {
            date: parts.date,
            // `""` for a date-only cell, while `value.time` is null `[cvs§2 date]`.
            time: parts.time ?? '',
            icon: value.icon ?? null,
            updated_at: toFragmentTimestamp(value.changed_at),
        };
    },
    /**
     * No webhook recording for this type: the conservative pass-through is monday's own `value`
     * object `[whk§7]`, which is what `toMondayValue` already produces (q330).
     */
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    /** Facet and group by **day** (ASSUMED, Q171); the time never enters the bucket key. */
    bucketsOf(value) {
        return value?.date ? [{ key: value.date, label: value.date }] : [];
    },
    filterSql(ctx) {
        return defaultFilterSql('date', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map